import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { productService } from '../api/products';
import { Product } from '../types';
import { Search, Grid, List, SlidersHorizontal } from 'lucide-react';
import { usePricingRefresh } from '../hooks/usePricingRefresh';
import { getProductSellingPrice } from '../utils/pricing';

const SearchPage = () => {
  const pricingRefresh = usePricingRefresh();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    category: '',
    minPrice: 0,
    maxPrice: 10000,
    brand: ''
  });

  const searchProducts = useCallback(async () => {
    if (!query.trim()) {
      setProducts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use the new search API
      const response = await productService.searchProducts(query, 50);
      
      if (response.success && response.data) {
        let results = response.data;
        setSuggestions(response.suggestions || []);
        
        // Apply client-side filters
        if (filters.category) {
          results = results.filter((p: Product) => 
            p.category?.toString().toLowerCase().includes(filters.category.toLowerCase())
          );
        }
        
        if (filters.brand) {
          results = results.filter((p: Product) => 
            p.brand?.toString().toLowerCase().includes(filters.brand.toLowerCase())
          );
        }
        
        if (filters.minPrice > 0) {
          results = results.filter((p: Product) => getProductSellingPrice(p) >= filters.minPrice);
        }
        
        if (filters.maxPrice < 10000) {
          results = results.filter((p: Product) => getProductSellingPrice(p) <= filters.maxPrice);
        }
        
        // Apply sorting
        results.sort((a: any, b: any) => {
          switch (sortBy) {
            case 'price-low':
              return (a.price?.selling || a.price || 0) - (b.price?.selling || b.price || 0);
            case 'price-high':
              return (b.price?.selling || b.price || 0) - (a.price?.selling || a.price || 0);
            case 'rating':
              return (b.rating?.average || b.rating || 0) - (a.rating?.average || a.rating || 0);
            case 'name':
              return a.name.localeCompare(b.name);
            default: // relevance - use API order (already sorted by relevance)
              return 0;
          }
        });
        
        setProducts(results);
      } else {
        setProducts([]);
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search products. Please try again.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [query, sortBy, filters, pricingRefresh]);

  useEffect(() => {
    searchProducts();
  }, [searchProducts]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => searchProducts()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <Search className="w-24 h-24 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No products found</h2>
            <p className="text-gray-600 mb-8">Try searching with different keywords or browse categories.</p>
            <Link to="/" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-primary-600">Home</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">Search Results</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Search Header with Input */}
        <div className="mb-8">
          <div className="max-w-2xl mb-6">
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                const newQuery = (e.target as HTMLFormElement).search.value;
                if (newQuery.trim()) {
                  window.location.href = `/search?q=${encodeURIComponent(newQuery)}`;
                }
              }}
              className="relative"
            >
              <input
                type="text"
                name="search"
                defaultValue={query}
                placeholder="Search for products..."
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              />
              <button
                type="submit"
                title="Search products"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>
            </form>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {products.length > 0 ? `${products.length} results for "${query}"` : `Search results for "${query}"`}
          </h1>
          
          {/* Related/Suggested Searches */}
          {suggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="text-sm text-gray-500">Related:</span>
              {suggestions.slice(0, 5).map((suggestion, index) => (
                <Link
                  key={index}
                  to={`/search?q=${encodeURIComponent(suggestion)}`}
                  className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                >
                  {suggestion}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-8">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="w-64 bg-white rounded-lg p-6 h-fit">
              <h3 className="font-semibold text-lg mb-4">Filters</h3>
              
              {/* Category Filter */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Category</h4>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  aria-label="Filter by category"
                  title="Filter by category"
                >
                  <option value="">All Categories</option>
                  <option value="Electronics">Electronics</option>
                  <option value="Watches">Watches</option>
                  <option value="Beauty">Beauty</option>
                  <option value="Shoes">Shoes</option>
                  <option value="Accessories">Accessories</option>
                </select>
              </div>

              {/* Brand Filter */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Brand</h4>
                <input
                  type="text"
                  value={filters.brand}
                  onChange={(e) => setFilters({...filters, brand: e.target.value})}
                  placeholder="Enter brand name"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Price Range</h4>
                <div className="space-y-3">
                  <input
                    type="number"
                    value={filters.minPrice}
                    onChange={(e) => setFilters({...filters, minPrice: Number(e.target.value)})}
                    placeholder="Min price"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  <input
                    type="number"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({...filters, maxPrice: Number(e.target.value)})}
                    placeholder="Max price"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <button
                onClick={() => setFilters({ category: '', minPrice: 0, maxPrice: 10000, brand: '' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1">
            {/* Controls */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                {/* View Mode */}
                <div className="flex border rounded-lg">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'text-gray-600'}`}
                    aria-label="Grid view"
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'text-gray-600'}`}
                    aria-label="List view"
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border rounded-lg px-4 py-2"
                  aria-label="Sort products"
                  title="Sort products"
                >
                  <option value="relevance">Sort by Relevance</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Customer Rating</option>
                  <option value="name">Name: A to Z</option>
                </select>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 border rounded-lg px-4 py-2"
                aria-label="Toggle filters"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
              </button>
            </div>

            {/* Results */}
            {error ? (
              <div className="text-center py-12">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Search Error</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <button onClick={searchProducts} className="btn-primary">
                  Try Again
                </button>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-4">
                  We couldn't find any products matching "{query}"
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">Try:</p>
                  <ul className="text-sm text-gray-500">
                    <li>• Checking your spelling</li>
                    <li>• Using more general terms</li>
                    <li>• Trying different keywords</li>
                  </ul>
                </div>
                <Link to="/" className="btn-primary mt-6 inline-block">
                  Browse All Products
                </Link>
              </div>
            ) : (
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                  : 'grid-cols-1'
              }`}>
                {products.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    variant={viewMode}
                    showWishlist={true}
                    showAddToCart={true}
                    showRating={true}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
